import { useEffect, useRef, useState } from "react";
import request from '@/utils/axios';

/**
 * @typedef {'idle' | 'pending' | 'success' | 'error'} FetchStatus
 * 
 * @typedef CMSSearchResult
 * @property {number} Cat1Id
 * @property {number} SitePageId
 * @property {string} PageTitle
 * @property {string} [ExecutiveSummary]
 * @property {string} [PublishDate]
 * @property {string} [PublishDateTime]
 */

/** 
 * @param {Record<string, unknown>} fieldOptions 
 * @param {boolean} shouldFetch
 * @param {boolean} loading
 * @returns {{
 *   results: CMSSearchResult[];
 *   status: FetchStatus;
 * }}
 */
export default function usePublicSearch(fieldOptions, shouldFetch, loading) {
  const payload = generatePublicSearchPayload(fieldOptions);
  const [results, setResults] = useState(/** @type {CMSSearchResult[]} */([]));
  const [status, setStatus] = useState(/** @type {FetchStatus} */('idle'))

  const rq = async () => {
    setStatus('pending');
    try {      
      /** @type {import("axios").AxiosResponse<{ Results: CMSSearchResult[] }>} */
      const response = await request({
        method: 'POST',
        url: '/cms/publicsearch',
        data: payload,
        triggerLoad: false,
      });
  
      if (response.status < 300) {
        setResults(response.data?.Results ?? []);
        setStatus('success');
      } else {
        setResults([]);
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  }

  useEffect(() => {
    if (!loading) rq();
  }, [loading]);

  return { results, status }
};

const generatePublicSearchPayload = fieldOptions => {
  return {
    SearchId: null,
    PersonId: null,
    SearchDescription: '',
    FieldOptions: {
      SearchText: '',
      States: [],
      LinesOfBusiness: [],
      Subjects: [],
      Publications: [],
      DayRanges: [],
      PostingTypes: [],
      Committees: [],
      ContentItems: [],
      DateRanges: [],
      Page: null,
      PageSize: null,
      SortField: 'CREATEDDATE',
      SortOrder: 'DESC',
      ...fieldOptions,
    },
  };
};
